/**
 * The Cognito password policy, restated in words a person can act on.
 *
 * These must stay in step with `lib/auth-stack.ts`'s `passwordPolicy` (minLength 8, lowercase,
 * uppercase, digits). Duplicating them here is deliberate: the alternative is what the app did before,
 * which was to say nothing up front and then surface Cognito's own sentence verbatim --
 * "Password did not conform with policy: Password must have uppercase characters" -- one violated rule
 * at a time, so a password missing two things took two round trips to discover (UX review F7).
 *
 * Keeping the list here also means the rules can be shown live as someone types, which is the only
 * version of this that never produces an error at all.
 */
export interface PasswordRule {
  /** Written to complete the sentence "Your password still needs: …". */
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { label: "at least 8 characters", test: (p) => p.length >= 8 },
  { label: "a lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "an uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "a number", test: (p) => /[0-9]/.test(p) },
];

/** The rules this password does not yet satisfy, in the order they're displayed. */
export function unmetPasswordRules(password: string): PasswordRule[] {
  return PASSWORD_RULES.filter((rule) => !rule.test(password));
}

/** True when Cognito would accept it, so callers can gate a submit without re-deriving the policy. */
export function passwordMeetsPolicy(password: string): boolean {
  return unmetPasswordRules(password).length === 0;
}
